import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import JobHistoryDetailsCard from './JobHistoryDetailsCard';
import CompartmentalizedTranscript from './CompartmentalizedTranscript';
import ContentSelectionButtons from './ContentSelectionButtons';
import MarkdownContentView from './MarkdownContentView';
import BubbleAlert from './modal/BubbleAlert';
import { getJob, JobStatus, addBlockedJob, triggerAnalysisJob, getJobsByParentJobId, setupPeriodicRefresh, cancelPeriodicRefresh } from './jobHistoryManager';
import { contentTypes } from '../nip105-client';
import { Cog, Sparkles, ArrowRight, X } from 'lucide-react';

const JobHistoryDetailsComponent = ({ jobId, onBack, onRunAnalysis, setCustomPrompt }) => {
    const [job, setJob] = useState(null);
    const [contentData, setContentData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [isFromContentSelection, setIsFromContentSelection] = useState(false);
    const [bubbleMessage, setBubbleMessage] = useState('');
    const [showBubble, setShowBubble] = useState(false);
    const [showJobSubmittedModal, setShowJobSubmittedModal] = useState(false);
    const [submittedJobId, setSubmittedJobId] = useState(null);
    const [submittedJobType, setSubmittedJobType] = useState('');
    const [isSubmittingJob, setIsSubmittingJob] = useState(false);
    const refreshIntervalRef = useRef(null);
    const ContentType = {
        TRANSCRIPT: 'Transcript',
        ANALYSIS: 'Analysis',
        UNKNOWN: 'Unknown'
    };

    const showBubbleAlert = (message) => {
        setBubbleMessage(message);
        setShowBubble(true);
        setTimeout(() => setShowBubble(false), 4000);
    }

    const fetchJobData = useCallback(() => {
        const jobData = getJob(jobId);
        setJob(jobData);
    }, [jobId]);

    const pollRetryCountRef = useRef(0);
    const MAX_POLL_RETRIES = 60; // ~5 minutes of polling at 5s intervals
    const MAX_ERROR_RETRIES = 3;
    const errorRetryCountRef = useRef(0);

    // Determine the correct endpoint based on job type
    const getResultEndpoint = useCallback((jobData, contentJobId) => {
        const isTranscriptJob = jobData?.inputField1 === "Transcript from Link" || jobData?.inputField1 === "Transcript from Upload";
        if (isTranscriptJob) {
            return `https://whispr-v3-w-caching-ex8zk.ondigitalocean.app/WHSPR/${contentJobId}/get_result`;
        } else {
            // Analysis jobs use the yt2-blogpost-agent endpoint
            return `https://yt2-blogpost-agent-w2x7c.ondigitalocean.app/AnalyzeTranscript/${contentJobId}/get_result`;
        }
    }, []);

    const fetchContent = useCallback(async (contentJobId, isPolling = false, jobData = null) => {
        if (!jobData) {
            console.error('fetchContent called without jobData');
            setError('Job data not available');
            setIsLoading(false);
            return;
        }
        
        if (!isPolling) {
            setIsLoading(true);
            pollRetryCountRef.current = 0;
            errorRetryCountRef.current = 0;
        }
        setError(null);
        try {
            const endpoint = getResultEndpoint(jobData, contentJobId);
            console.log(`Fetching content from: ${endpoint}`);
            const response = await axios.get(endpoint);
            const data = response.data;
            
            // Reset error counter on successful response
            errorRetryCountRef.current = 0;
            
            // Check if job is still working - keep polling
            if (data.state === 'WORKING' || data.queueInfo?.status === 'PROCESSING') {
                pollRetryCountRef.current++;
                console.log(`Job still processing (poll ${pollRetryCountRef.current}/${MAX_POLL_RETRIES}), will poll again...`);
                
                if (pollRetryCountRef.current >= MAX_POLL_RETRIES) {
                    setError('Job is taking longer than expected. Please check back later.');
                    setIsLoading(false);
                    return;
                }
                
                // Keep isLoading true and poll again after delay
                setTimeout(() => {
                    fetchContent(contentJobId, true, jobData);
                }, 5000);
                return; // Don't set contentData or turn off loading
            }
            
            // Job is done - set the content
            setContentData(data);
            setIsLoading(false);
        } catch (err) {
            console.error('Error fetching data:', err);
            errorRetryCountRef.current++;
            
            // On error, retry a few times before showing error
            if (errorRetryCountRef.current < MAX_ERROR_RETRIES) {
                console.log(`Poll failed (attempt ${errorRetryCountRef.current}/${MAX_ERROR_RETRIES}), retrying...`);
                setTimeout(() => {
                    fetchContent(contentJobId, true, jobData);
                }, 5000);
                return;
            }
            
            setError('Failed to load data. Please try again later.');
            setIsLoading(false);
        }
    }, [getResultEndpoint]);

    const processRelevantJobChanges = useCallback(() => {
        console.log('Processing relevant job changes...');
        const parentJobId = jobId.startsWith('T-') ? jobId.split('-')[1] : jobId;
        const relevantJobs = getJobsByParentJobId(parentJobId).filter(job => job.status !== JobStatus.AWAITING_PARENT_JOB);
        if(relevantJobs.length > 0) {
            console.log(`relevantJobs: ${JSON.stringify(relevantJobs, null, 2)}`);
            const latestJob = relevantJobs[0];
            setJob(prevJob => {
                if (JSON.stringify(prevJob) !== JSON.stringify(latestJob)) {
                    console.log('Updating job state:', latestJob);
                    return latestJob;
                }
                return prevJob;
            });
            if (latestJob.status === JobStatus.DONE) {
                console.log('Job is done, cancelling polling');
                fetchContent(latestJob.jobId, false, latestJob);
                if (refreshIntervalRef.current) {
                    cancelPeriodicRefresh(refreshIntervalRef.current);
                    refreshIntervalRef.current = null;
                }
                setIsLoading(false);
            }
        }
    }, [jobId, fetchContent]);

    const pollParentJob = useCallback(() => {
        console.log('Starting to poll parent job...');
        setIsLoading(true);
        setTimeout(() => {
            if (!refreshIntervalRef.current) {
                refreshIntervalRef.current = setupPeriodicRefresh(() => {
                    processRelevantJobChanges();
                }, 5000); // Poll every 5 seconds
            }
        }, 2000); // 2-second delay before starting to poll
    }, [processRelevantJobChanges]);

    const handleRunButtonClick = useCallback(async (selectedOption, customPrompt) => {
        console.log(`JobHistoryDetailsComponent handleRunButtonClick: ${selectedOption} ${customPrompt}`);
        const displayedOption = contentTypes.find(option => option.id === selectedOption)?.label || selectedOption;
        const serviceName = 'Jamie';
        const inputField1 = displayedOption;
        const inputField2 = customPrompt;
        const parentJobId = jobId; // Use the current job ID as the parent
        const outputType = displayedOption;

        // Set loading state immediately
        setIsSubmittingJob(true);

        // Add blocked job
        const newJob = addBlockedJob(serviceName, inputField1, inputField2, parentJobId, outputType, 'Waiting for analysis to start');

        try {
            // Trigger the analysis job and wait for the real ID
            const result = await triggerAnalysisJob(newJob, parentJobId);
            
            if (result.submitted && result.realJobId) {
                // Use the real paymentHash as the jobId
                setSubmittedJobId(result.realJobId);
            } else {
                // Fallback to temp ID if submission didn't return real ID
                setSubmittedJobId(newJob.jobId);
            }
            
            setSubmittedJobType(displayedOption);
            setShowJobSubmittedModal(true);
        } catch (err) {
            console.error('Error triggering analysis job:', err);
            showBubbleAlert('Failed to submit analysis job. Please try again.');
        } finally {
            setIsSubmittingJob(false);
        }
    }, [jobId]);

    const handleGoToNewJob = useCallback(() => {
        if (submittedJobId) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('tab', 'history');
            newUrl.searchParams.set('jobId', submittedJobId);
            window.history.pushState({}, '', newUrl);
            window.location.reload(); // Reload to navigate to the new job
        }
        setShowJobSubmittedModal(false);
    }, [submittedJobId]);

    const handleStayHere = useCallback(() => {
        setShowJobSubmittedModal(false);
        showBubbleAlert('Analysis job running in the background!');
    }, []);

    useEffect(() => {
        const jobData = getJob(jobId);
        setJob(jobData);
        
        if (jobId.startsWith('T-')) {
            pollParentJob();
        } else {
            fetchContent(jobId, false, jobData);
        }

        return () => {
            if (refreshIntervalRef.current) {
                cancelPeriodicRefresh(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [fetchContent, jobId, pollParentJob]);

    useEffect(() => {
        if (job && getContentType() !== ContentType.UNKNOWN && job.status === JobStatus.WORKING) {
            setJob(prevJob => ({ ...prevJob, status: JobStatus.DONE }));
        }
    }, [contentData, job]);

    const handleActionSelect = useCallback((action) => {
        setSelectedAction(action.id);
        setIsFromContentSelection(true);
        console.log('Selected action:', action);
    }, []);

    const handleCustomPromptChange = useCallback((newPrompt) => {
        if (setCustomPrompt && typeof setCustomPrompt === 'function') {
            setCustomPrompt(newPrompt);
        }
    }, [setCustomPrompt]);

    const getContentType = useCallback(() => {
        if(contentData && contentData.transcript){
            return ContentType.ANALYSIS;
        }
        else if(contentData && contentData.channels){
            return ContentType.TRANSCRIPT;
        }
        else{
            return ContentType.UNKNOWN;
        }
    }, [contentData]);

    const isTranscript = job?.inputField1 === "Transcript from Link" || job?.inputField1 === "Transcript from Upload";
    const isAnalysisJob = ["Summary", "Show Notes", "YouTube Description", "Article", "Quotes"].includes(job?.inputField1);

    const renderContent = useCallback(() => {
        if (!contentData || !job) return null;
        const type = getContentType();
        switch(type){
            case ContentType.ANALYSIS:
                return (
                    <MarkdownContentView
                        title={`${job.inputField1} Results:`}
                        content={contentData.transcript}
                        onCopy={(text) => navigator.clipboard.writeText(text)}
                    />
                );
            case ContentType.TRANSCRIPT:
                return (
                    <CompartmentalizedTranscript
                        title={`${job.inputField1} Results:`}
                        transcriptData={contentData}
                        onRunAnalysis={onRunAnalysis}
                        setCustomPrompt={handleCustomPromptChange}
                        initialToggleTab="Transcript"
                        showOptionsOnOpen={true}
                        selectedViewAction={selectedAction}
                        isFromContentSelection={isFromContentSelection}
                        onRunButtonClick={handleRunButtonClick}
                        isLoading={isSubmittingJob}
                    />
                );
            default:
                return <p>No content available for this job type.</p>;
        }
    }, [contentData, job, getContentType, onRunAnalysis, handleCustomPromptChange, selectedAction, isFromContentSelection, handleRunButtonClick, isSubmittingJob]);

    if (!job) {
        return <div>Loading job details...</div>;
    }

    return (
        <div className="job-details-container bg-black text-white p-6">
            {/* Job Submitted Modal */}
            {showJobSubmittedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                    <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 border border-orange-500/30 shadow-2xl">
                        {/* Close button */}
                        <button 
                            onClick={handleStayHere}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        >
                            <X size={24} />
                        </button>
                        
                        {/* Icon */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-full p-4">
                                <Sparkles size={40} className="text-white" />
                            </div>
                        </div>
                        
                        {/* Content */}
                        <h2 className="text-2xl font-bold text-center text-white mb-2">
                            Analysis Job Submitted!
                        </h2>
                        <p className="text-gray-300 text-center mb-6">
                            Your <span className="text-orange-400 font-semibold">{submittedJobType}</span> analysis is now processing in the background.
                        </p>
                        
                        {/* Buttons */}
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={handleGoToNewJob}
                                className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                Go to New Job
                                <ArrowRight size={20} />
                            </button>
                            <button
                                onClick={handleStayHere}
                                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200"
                            >
                                Stay Here
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto" style={{marginTop:'125px'}}>
                <button 
                    onClick={onBack}
                    className="text-xl font-bold mb-6 hover:text-orange-200"
                >
                    ← Back to Job History
                </button>
                <div className={`grid ${isTranscript ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
                    <div>
                        <JobHistoryDetailsCard job={job} isCompact={true} shouldShowFullDetails={true}/>
                    </div>
                    {isTranscript && (
                        <div>
                            <ContentSelectionButtons 
                                title={"Analysis Tools Shortcuts"} 
                                onActionSelect={handleActionSelect}
                            />
                        </div>
                    )}
                </div>
                <div className="mt-6">
                    {showBubble && <BubbleAlert message={bubbleMessage} />}
                    {isLoading && 
                    (<div className="flex flex-col items-center justify-center mt-20 bg-gray-800" style={{borderRadius:'16px', paddingTop:'24px',paddingBottom:'24px'}}>
                        <Cog size={64} className="text-white-500 mb-4" />
                        <p className="text-xl text-white-500">{job.status === JobStatus.AWAITING_PARENT_JOB ? "Awaiting parent job to complete!" : "Your Job is Running!"}</p>
                        <p className="text-white-400 mt-2">Wait here or come back later to see the results</p>
                        <div className="flex justify-center mt-2" role="status">
                            <svg aria-hidden="true" className="w-16 h-16 animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#4B5563"/>
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="white"/>
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                    )}
                    {error && <p className="text-red-500">{error}</p>}
                    {!isLoading && !error && renderContent()}
                </div>
            </div>
        </div>
    );
};

export default JobHistoryDetailsComponent;